import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Chip, Menu, MenuItem, Snackbar, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { authState, globalAlertState, useLogout } from '../api';
import { UpdatePasswordModal } from '../app/Account/AccountSettingPage/UpdatePasswordModal';
import { useTranslation } from 'react-i18next';
import { loadBalancerAPI } from '../api/load-balancer';
import TwoFAStateModal from '../app/TwoFactorAuthModal/TwoFAStateModal';
import TwoFactorAuthModal from '../app/TwoFactorAuthModal/TwoFactorAuthModal';
import DeviceExistsModal from '../app/TwoFactorAuthModal/DeviceExistsModal';
import RecoveryCodeDialog from '../app/TwoFactorAuthModal/RecoveryCodeModal';

export const UserLoginMenu = ({ onNavigate }: { onNavigate?: () => void }) => {
  const navigate = useNavigate();
  const authStateValue = useRecoilValue(authState);
  const [, setAlertState] = useRecoilState(globalAlertState);
  const [twoFAState, setTwoFAState] = useState(null);
  const [isOpenChangeTwoFAModal, setIsOpenChangeTwoFAModal] = useState(false);

  const logout = useLogout();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
  const [anchorElTwoFA, setAnchorElTwoFA] = useState<null | HTMLElement>(null);
  const [isTwoFAMenuOpen, setIsTwoFAMenuOpen] = useState(false);
  const { t } = useTranslation();
  const [isTwoFactorAuthModalOpen, setIsTwoFactorAuthModalOpen] = useState<boolean>(false);
  const [openDeviceExist, setOpenDeiviceExist] = useState<boolean>(false);
  const [openRecoveryCodeDialog, setOpenRecoveryCodeDialog] = useState<boolean>(false);
  const [recoveryCode, setRecoveryCode] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isGenerateCode, setIsGenerateCode] = useState(false);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleOpenTwoFAMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElTwoFA(event.currentTarget);
    setIsTwoFAMenuOpen(true);
  };

  const get2FAStatus = async () => {
    if (!authStateValue?.user?.id) return;
    try {
      const response = await loadBalancerAPI.apiInstance.getTwoFAState(authStateValue?.user?.id);
      if (response?.data) {
        setTwoFAState(response?.data?.twoFAState);
      }
    } catch {
      console.error('Fail to get 2FA state');
    }
  };

  useEffect(() => {
    if (authStateValue?.user?.id) {
      get2FAStatus();
    }
  }, [authStateValue?.user?.id, authStateValue?.user?.twoFAState]);

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setAnchorElTwoFA(null);
    setIsTwoFAMenuOpen(false);
  };

  const handleLogout = () => {
    handleCloseMenu();
    logout();
    navigate('/');
  };

  const handleChangePassword = () => {
    handleCloseMenu();
    setIsChangePasswordModalOpen(true);
  };

  const handleClose2FAState = () => {
    setIsOpenChangeTwoFAModal(false);
    setOpenDeiviceExist(false);
  };

  const handleGetRecoveryCode = async () => {
    if (!authStateValue?.user?.id) return;
    try {
      const response = await loadBalancerAPI.apiInstance.getRecoveryCode(authStateValue?.user?.id);
      const newRecoveryCode = response.data.data.recoveryCode;

      setRecoveryCode(newRecoveryCode);
      setOpenRecoveryCodeDialog(true);
    } catch {
      console.error(`Can't get recovery code`);
    }
  };

  const handleGenerateNewRecoveryCode = async () => {
    if (!authStateValue?.user?.id) return;
    try {
      const response = await loadBalancerAPI.apiInstance.generateNewRecoveryCode(authStateValue?.user?.id);
      const newRecoveryCode = response.data.data;
      if (newRecoveryCode) {
        handleGetRecoveryCode();
      }
    } catch {
      console.error(`Can't generate new recovery code`);
    }
  };

  const handleCreateNewDevice = () => {
    setIsTwoFactorAuthModalOpen(true);
    setIsOpenChangeTwoFAModal(false);
  };

  const handle2FAVerify = () => {
    setOpenDeiviceExist(false);
    handleGetRecoveryCode();
  };

  const handleOpenDeviceExist = () => {
    handleCloseMenu();
    setOpenDeiviceExist(true);
    setIsGenerateCode(true);
  };

  const handleCopyRecoveryCode = () => {
    navigator.clipboard
      .writeText(recoveryCode)
      .then(() => {
        setSnackbarOpen(true);
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
      });
  };

  const handleOpenRecoveryDialog = () => {
    handleGetRecoveryCode();
    setOpenRecoveryCodeDialog(true);
    setIsOpenChangeTwoFAModal(false);
  };

  const handleChangeState = () => {
    handleCloseMenu();
    setIsOpenChangeTwoFAModal(true);
    setIsGenerateCode(false);
  };

  return (
    <Box sx={{ mr: 1 }}>
      {!authStateValue.refreshToken ? (
        <Link to="/auth/login" onClick={onNavigate}>
          <Chip clickable label={t('userLoginMenuPage.login')} sx={{ minWidth: '100px', textAlign: 'center' }} />
        </Link>
      ) : (
        <>
          <Button
            id="user-action-button"
            variant="text"
            sx={{
              color: 'text.secondary',
              border: '1px solid',
              borderColor: 'text.secondary',
            }}
            onClick={handleOpenMenu}
          >
            <AccountCircleIcon sx={{ mr: 1 }} fontSize="small" />
            <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 90 }}>
              {authStateValue.user?.email ?? ''}
            </Typography>
            <ExpandMoreIcon fontSize="small" />
          </Button>
          <Menu
            id="user-action-menu"
            aria-labelledby="user-action-button"
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={handleCloseMenu}
          >
            <MenuItem onClick={handleChangePassword}>{t('userLoginMenuPage.changePassword')}</MenuItem>
            {(authStateValue?.user?.role === 1 || authStateValue?.user?.role === 2) && (
              <MenuItem onClick={handleOpenTwoFAMenu}>
                <Typography variant="body2">2FA</Typography>
                <ExpandMoreIcon fontSize="small" sx={{ ml: 1 }} />
              </MenuItem>
            )}
            <MenuItem onClick={handleLogout}>{t('userLoginMenuPage.logout')}</MenuItem>
          </Menu>
          <Menu
            anchorEl={anchorElTwoFA}
            open={isTwoFAMenuOpen}
            onClose={handleCloseMenu}
            PaperProps={{
              sx: {
                position: 'absolute',
                top: '100%',
                left: 0,
                mt: 1,
                zIndex: 1300,
              },
            }}
          >
            <MenuItem onClick={handleChangeState}>
              {twoFAState === null || twoFAState === false ? `Enable 2FA` : `Disable 2FA`}
            </MenuItem>
            {twoFAState === true && <MenuItem onClick={handleOpenDeviceExist}>Recovery Code</MenuItem>}
          </Menu>
        </>
      )}
      {isChangePasswordModalOpen && (
        <UpdatePasswordModal onClose={() => setIsChangePasswordModalOpen(false)} onCompleted={setAlertState} />
      )}
      {isOpenChangeTwoFAModal && (
        <TwoFAStateModal
          isOpen={isOpenChangeTwoFAModal}
          onClose={handleClose2FAState}
          isClickMenu={true}
          onCreateNewDevice={handleCreateNewDevice}
          isOpenRecoveryCodeDialog={handleOpenRecoveryDialog}
        />
      )}

      <TwoFactorAuthModal
        isOpen={isTwoFactorAuthModalOpen}
        onClose={() => setIsTwoFactorAuthModalOpen(false)}
        onVerify={() => setIsTwoFactorAuthModalOpen(false)}
      />

      {openDeviceExist && (
        <DeviceExistsModal
          isOpen={openDeviceExist}
          onClose={handleClose2FAState}
          onVerify={handle2FAVerify}
          isMenuClick={true}
        />
      )}
      {openRecoveryCodeDialog && (
        <RecoveryCodeDialog
          open={openRecoveryCodeDialog}
          onClose={() => setOpenRecoveryCodeDialog(false)}
          recoveryCode={recoveryCode}
          onCopy={handleCopyRecoveryCode}
          onGenerateNewCode={handleGenerateNewRecoveryCode}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message="Recovery code copied to clipboard"
      />
    </Box>
  );
};
